import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
// @mui
import {
  Box,
  Button,
  List,
  Badge,
  Divider,
  Typography,
  ListItemText,
  ListSubheader,
  ListItemAvatar,
  ListItemButton,
} from '@mui/material';
// utils
import { fToNow } from '../../../utils/formatTime';
import localizeValue from '../../../utils/localizeValue';
// components
import Avatar from '../../../components/Avatar';
import Iconify from '../../../components/Iconify';
import Scrollbar from '../../../components/Scrollbar';
import MenuPopover from '../../../components/MenuPopover';
import { IconButtonAnimate } from '../../../components/animate';
// hooks
import useLocales from '../../../hooks/useLocales';
import useAuth from '../../../hooks/useAuth';
// routes
import { PATH } from '../../../routes/paths';
// redux
import { useDispatch } from '../../../redux/store';
import { getActivity } from '../../../redux/slices/activity';

// ----------------------------------------------------------------------

export default function ActivityPopover() {
  const { translate } = useLocales();
  const { user } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [data, setData] = useState({});

  const [open, setOpen] = useState(null);

  useEffect(() => {
    async function fetchData() {
      try {
        const request = await dispatch(getActivity());
        setData(request?.data?.results || {});
      } catch (error) {
        console.error(error);
        setData({});
      }
    }
    fetchData();
  }, [dispatch, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleButton = () => {
    navigate(PATH.project.root);
    handleClose();
  };

  return (
    <>
      <IconButtonAnimate
        id="activity-icon"
        color={open ? 'primary' : 'default'}
        onClick={handleOpen}
        sx={{ width: 40, height: 40 }}
      >
        <Badge badgeContent={data?.count} color="error">
          <Iconify icon="eva:bell-fill" width={20} height={20} />
        </Badge>
      </IconButtonAnimate>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{ width: 360, p: 0, mt: 1.5, ml: 0.75 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">{translate('utils.activity.title')}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {translate('utils.activity.description', { count: data?.count })}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Scrollbar sx={{ height: 340 }}>
          <List
            disablePadding
            subheader={
              <ListSubheader disableSticky sx={{ py: 1, px: 2.5, typography: 'overline' }}>
                {translate('utils.activity.activities')}
              </ListSubheader>
            }
          >
            {data?.activities?.map((item) => (
              <ActivityItem key={item.id} item={item} onClose={() => setOpen(null)} />
            ))}
          </List>
        </Scrollbar>

        {user?.business?.featuresactive?.includes('feature.project') && (
          <>
            <Divider sx={{ borderStyle: 'dashed' }} />

            <Box sx={{ p: 1 }}>
              <Button id="gotoboard-button" fullWidth disableRipple onClick={handleButton}>
                {translate('utils.activity.gotoboard')}
              </Button>
            </Box>
          </>
        )}
      </MenuPopover>
    </>
  );
}

// ----------------------------------------------------------------------

ActivityItem.propTypes = {
  item: PropTypes.object,
  onClose: PropTypes.func,
};

function ActivityItem({ item, onClose }) {
  const { translate, currentLang } = useLocales();
  const { user } = useAuth();
  const isCreator = item.created_by.id === user.id;

  return (
    <ListItemButton
      sx={{ py: 1.5, px: 2.5, mt: '1px', cursor: item.related_record ? 'pointer' : 'default' }}
      component={RouterLink}
      to={item.related_record || '#'}
      onClick={() => (item.related_record ? onClose() : undefined)}
    >
      <ListItemAvatar>
        <Avatar src={item.created_by.image?.image} alt={localizeValue(item.created_by.name)} color="primary">
          {localizeValue(item.created_by.name)[0] || ''}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {translate(`utils.activity.activity.${isCreator ? 'pre' : 'user'}`)}
              {!isCreator && (
                <Typography component="span" variant="subtitle2">
                  &nbsp;{localizeValue(item.created_by.name)}
                </Typography>
              )}
              &nbsp;{translate('utils.activity.activity.activity')}
              <Typography component="span" variant="subtitle2">
                &nbsp;{item.name}
              </Typography>
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'break-spaces' }}>
              {item.description}
            </Typography>
          </>
        }
        secondary={
          <Typography
            variant="caption"
            sx={{
              mt: 0.5,
              display: 'flex',
              alignItems: 'center',
              color: 'text.disabled',
            }}
          >
            <Iconify icon="eva:clock-outline" sx={{ mr: 0.5, width: 16, height: 16 }} />
            {item.due && <>{translate('utils.activity.activity.due')}&nbsp;</>}
            {fToNow(item.due || item.created_at, currentLang.value)}
          </Typography>
        }
      />
    </ListItemButton>
  );
}
